import { computed, onMounted, ref, watch } from "vue";
import type { TableProps } from "./TableProps";
import type { TableContext, ColumnWidth } from "./useTable.types";
import { createSplitterCore } from "composables/useSplitter";
import { getElementByUid } from "composables/useStateStore";

/** Minimum column width in pixels during resize. */
const MIN_COLUMN_WIDTH = 50;

// ── Column width store helpers ────────────────────────────────────────────────

const readColumnWidths = (tableProps: TableContext["tableProps"]): Map<number, ColumnWidth> => {
  const raw = tableProps.value?.columnWidths;
  return raw instanceof Map ? raw : new Map();
};

const writeColumnWidths = (
  stateStore: TableContext["stateStore"],
  tableUid: TableContext["tableUid"],
  widths: Map<number, ColumnWidth>,
) => {
  if (!tableUid.value) return;
  stateStore.setValue<TableProps>(tableUid.value, "columnWidths", new Map(widths));
};

// ── Composable ────────────────────────────────────────────────────────────────

export const useTableResize = (ctx: TableContext) => {
  const cellRef = ref<HTMLElement>();
  const isColumnResizable = computed(() => ctx.tableProps.value?.columnSizing === "resizable");

  // Use the element registry + DOM siblings for a timing-safe last-column check.
  // At mount time all siblings are in the DOM (CE elements exist before they upgrade).
  const isLastColumn = ref(false);
  onMounted(() => {
    const uid = ctx.context.uid;
    if (uid === null) return;
    const host = getElementByUid(uid);
    const next = host?.nextElementSibling;
    // Siblings are in the DOM (by tag name) before they register in the WeakMap,
    // so use tag name to check rather than getElementUid which isn't ready yet.
    isLastColumn.value = !next || next.tagName.toLowerCase() !== "e-table-header-cell";
  });

  // Each column gets its own core; expand mode only modifies the single before-panel.
  const stored = readColumnWidths(ctx.tableProps).get(ctx.currentColumnIndex.value);
  const core = createSplitterCore({
    unit: "px",
    resizeMode: "expand",
    direction: "horizontal",
    keyboardStep: 10,
  });
  const panelIndex = core.registerPanel({ minSize: MIN_COLUMN_WIDTH, defaultSize: stored?.width });
  const handleIndex = core.registerHandle();

  // Sync core size → store whenever it changes (synchronous flush for predictable tests).
  watch(
    () => core.sizes.value[panelIndex],
    (width) => {
      if (!width) return;
      const colIdx = ctx.currentColumnIndex.value;
      const widths = readColumnWidths(ctx.tableProps);
      widths.set(colIdx, { index: colIdx, width, minWidth: MIN_COLUMN_WIDTH });
      writeColumnWidths(ctx.stateStore, ctx.tableUid, widths);
    },
    { flush: "sync" },
  );

  const startResize = (e: PointerEvent) => {
    if (!isColumnResizable.value || !ctx.tableUid.value) return;

    // Initialise to current DOM width so the first drag is accurate.
    const currentWidth = cellRef.value?.offsetWidth ?? MIN_COLUMN_WIDTH;
    const newSizes = [...core.sizes.value];
    newSizes[panelIndex] = currentWidth;
    core.sizes.value = newSizes;

    const handler = core.makeDragHandler(handleIndex, () => 0);
    handler(e);
  };

  return {
    cellRef,
    isColumnResizable,
    isLastColumn,
    getStoredColumnWidths: () => readColumnWidths(ctx.tableProps),
    startResize,
  };
};
